import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Loader2, User, Clock, Tag, ExternalLink } from 'lucide-react';
import Reveal from '../../components/public/Reveal';
import api from '../../lib/api';

function toHtml(content) {
  if (!content) return '';
  if (content.includes('<')) return content;
  return content.split('\n').filter(Boolean).map((p) => `<p>${p}</p>`).join('');
}

export default function NoticiaDetalle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/news/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cnib-navy">
        <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-cnib-navy px-4 text-center">
        <h1 className="font-heading text-3xl font-bold text-white">Noticia no encontrada</h1>
        <Link to="/noticias" className="flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 font-bold text-white">
          <ArrowLeft className="h-4 w-4" /> Volver a noticias
        </Link>
      </div>
    );
  }

  return (
    <article>
      <section className="hero-grain relative overflow-hidden bg-cnib-navy pb-14 pt-32 md:pt-40">
        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal y={16}>
            <Link
              to="/noticias"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-orange-400"
              data-testid="news-detail-back"
            >
              <ArrowLeft className="h-4 w-4" /> Todas las noticias
            </Link>
            {article.volanta && (
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.25em] text-orange-500">{article.volanta}</p>
            )}
            <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
              {article.title}
            </h1>
            {article.excerpt && <p className="mt-5 text-lg leading-relaxed text-slate-300">{article.excerpt}</p>}
            <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-slate-400">
              <span className="rounded-full bg-orange-500 px-3 py-1 font-bold text-white">{article.category}</span>
              <span className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {new Date(article.created_at).toLocaleDateString('es-BO', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {article.author}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {article.reading_minutes || 2} min de lectura</span>
            </div>
            {article.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-xs text-slate-300">
                    <Tag className="h-3 w-3 text-orange-500" /> {tag}
                  </span>
                ))}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {article.image_url && (
        <div className="mx-auto -mt-2 max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <img src={article.image_url} alt={article.title} className="w-full rounded-3xl shadow-2xl" />
          </Reveal>
        </div>
      )}

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="article-body" data-testid="article-content" dangerouslySetInnerHTML={{ __html: toHtml(article.content) }} />
        {article.external_source && (
          <p className="mt-10 flex items-center gap-2 border-t border-slate-200 pt-6 text-sm text-slate-500">
            <ExternalLink className="h-4 w-4 text-cnib-blue" />
            Fuente:{' '}
            {article.external_url ? (
              <a href={article.external_url} target="_blank" rel="noopener noreferrer" className="font-semibold text-cnib-blue underline">
                {article.external_source}
              </a>
            ) : (
              <span className="font-semibold">{article.external_source}</span>
            )}
          </p>
        )}
      </section>
    </article>
  );
}
